export type UnitCategoryKey = 'length' | 'mass' | 'area' | 'volume' | 'temperature' | 'speed'

export type UnitKey =
  | 'mm' | 'cm' | 'm' | 'km' | 'in' | 'ft'
  | 'g' | 'kg' | 't' | 'lb'
  | 'cm2' | 'm2' | 'km2' | 'ha'
  | 'ml' | 'l' | 'cm3' | 'm3'
  | 'c' | 'f' | 'k'
  | 'ms' | 'kmh' | 'mph'

export interface UnitDefinition {
  key: UnitKey
  label: string
  abbreviation: string
  factor?: number
}

export interface UnitCategory {
  key: UnitCategoryKey
  label: string
  units: readonly UnitDefinition[]
}

export interface ConverterSelection {
  category: UnitCategoryKey
  fromUnit: UnitKey
  toUnit: UnitKey
}

export interface ConversionState {
  status: 'empty' | 'invalid' | 'ready'
  value: number | null
  formatted: string | null
  snapshot: string | null
  error: 'invalid_number' | 'units_match' | null
}

export const unitCategories: readonly UnitCategory[] = [
  {
    key: 'length',
    label: 'Длина',
    units: [
      { key: 'mm', label: 'Миллиметры', abbreviation: 'мм', factor: 0.001 },
      { key: 'cm', label: 'Сантиметры', abbreviation: 'см', factor: 0.01 },
      { key: 'm', label: 'Метры', abbreviation: 'м', factor: 1 },
      { key: 'km', label: 'Километры', abbreviation: 'км', factor: 1000 },
      { key: 'in', label: 'Дюймы', abbreviation: 'дюйм', factor: 0.0254 },
      { key: 'ft', label: 'Футы', abbreviation: 'фут', factor: 0.3048 },
    ],
  },
  {
    key: 'mass',
    label: 'Масса',
    units: [
      { key: 'g', label: 'Граммы', abbreviation: 'г', factor: 0.001 },
      { key: 'kg', label: 'Килограммы', abbreviation: 'кг', factor: 1 },
      { key: 't', label: 'Тонны', abbreviation: 'т', factor: 1000 },
      { key: 'lb', label: 'Фунты', abbreviation: 'lb', factor: 0.45359237 },
    ],
  },
  {
    key: 'area',
    label: 'Площадь',
    units: [
      { key: 'cm2', label: 'Квадратные сантиметры', abbreviation: 'см²', factor: 0.0001 },
      { key: 'm2', label: 'Квадратные метры', abbreviation: 'м²', factor: 1 },
      { key: 'km2', label: 'Квадратные километры', abbreviation: 'км²', factor: 1_000_000 },
      { key: 'ha', label: 'Гектары', abbreviation: 'га', factor: 10_000 },
    ],
  },
  {
    key: 'volume',
    label: 'Объём',
    units: [
      { key: 'ml', label: 'Миллилитры', abbreviation: 'мл', factor: 0.001 },
      { key: 'l', label: 'Литры', abbreviation: 'л', factor: 1 },
      { key: 'cm3', label: 'Кубические сантиметры', abbreviation: 'см³', factor: 0.001 },
      { key: 'm3', label: 'Кубические метры', abbreviation: 'м³', factor: 1000 },
    ],
  },
  {
    key: 'temperature',
    label: 'Температура',
    units: [
      { key: 'c', label: 'Градусы Цельсия', abbreviation: '°C' },
      { key: 'f', label: 'Градусы Фаренгейта', abbreviation: '°F' },
      { key: 'k', label: 'Кельвины', abbreviation: 'K' },
    ],
  },
  {
    key: 'speed',
    label: 'Скорость',
    units: [
      { key: 'ms', label: 'Метры в секунду', abbreviation: 'м/с', factor: 1 },
      { key: 'kmh', label: 'Километры в час', abbreviation: 'км/ч', factor: 1 / 3.6 },
      { key: 'mph', label: 'Мили в час', abbreviation: 'mph', factor: 0.44704 },
    ],
  },
]

const NUMBER_PATTERN = /^[+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)$/

export function categoryByKey(key: UnitCategoryKey): UnitCategory {
  return unitCategories.find((category) => category.key === key) ?? unitCategories[0]!
}

export function unitByKey(category: UnitCategoryKey, key: UnitKey): UnitDefinition | null {
  return categoryByKey(category).units.find((unit) => unit.key === key) ?? null
}

export function parseConverterNumber(input: string): number | null {
  const normalized = input.trim().replace(',', '.')
  if (!NUMBER_PATTERN.test(input.trim())) return null
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

function toCelsius(value: number, unit: UnitKey): number {
  if (unit === 'f') return (value - 32) * (5 / 9)
  if (unit === 'k') return value - 273.15
  return value
}

function fromCelsius(value: number, unit: UnitKey): number {
  if (unit === 'f') return value * (9 / 5) + 32
  if (unit === 'k') return value + 273.15
  return value
}

export function convertUnit(value: number, selection: ConverterSelection): number {
  if (selection.category === 'temperature') {
    return fromCelsius(toCelsius(value, selection.fromUnit), selection.toUnit)
  }

  const from = unitByKey(selection.category, selection.fromUnit)
  const to = unitByKey(selection.category, selection.toUnit)
  if (!from?.factor || !to?.factor) return Number.NaN
  return (value * from.factor) / to.factor
}

export function formatConvertedNumber(value: number): string {
  const normalized = Object.is(value, -0) ? 0 : value
  return new Intl.NumberFormat('ru-RU', {
    maximumSignificantDigits: 12,
    useGrouping: true,
  }).format(normalized)
}

export function calculateConversion(input: string, selection: ConverterSelection): ConversionState {
  const trimmed = input.trim()
  if (trimmed === '') {
    return { status: 'empty', value: null, formatted: null, snapshot: null, error: null }
  }

  const value = parseConverterNumber(trimmed)
  const snapshot = `${selection.category}|${selection.fromUnit}|${selection.toUnit}|${trimmed.replace(',', '.')}`
  if (value === null) {
    return { status: 'invalid', value: null, formatted: null, snapshot, error: 'invalid_number' }
  }
  if (selection.fromUnit === selection.toUnit) {
    return { status: 'invalid', value: null, formatted: null, snapshot, error: 'units_match' }
  }

  const converted = convertUnit(value, selection)
  if (!Number.isFinite(converted)) {
    return { status: 'invalid', value: null, formatted: null, snapshot, error: 'invalid_number' }
  }

  return {
    status: 'ready',
    value: converted,
    formatted: formatConvertedNumber(converted),
    snapshot,
    error: null,
  }
}
